import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@leadsnavi/ui-kit';
import cn from 'classnames';
import { Ellipse } from '@/common/base/ellipse';
import type { TableBlock as TableBlockType } from '../types';
import { BlockEmptyState } from './BlockEmptyState';

// ─────────────────────────────────────────────
// TableBlock — 数据表格
// Figma node 10320-25003：灰底(#F8F8F7) + 圆角8px + header 12px/500/#737373
// 行间分隔线 #E5E5E5 | 最后一列 YoY 正绿(#059669)负红(#B91C1C)
// ─────────────────────────────────────────────

/** 判断最后一列（变化率列）的颜色 class */
function getLastColColorClass(value: string): string {
  if (!value || value === 'N/A' || value === '—') return 'text-[#737373]';
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) return 'text-[#059669]';
  if (trimmed.startsWith('-')) return 'text-[#B91C1C]';
  return 'text-[#262626]';
}

export function TableBlock({ block }: { block: TableBlockType }) {
  const { title, columns = [], rows = [] } = block;

  const totalCols = columns.length;

  if (columns.length === 0 && rows.length === 0) return <BlockEmptyState label="表格" />;

  return (
    <div className="flex flex-col gap-2">
      {title && <p className="text-[13px] font-medium text-[#262626]">{title}</p>}

      <div className="bg-[#F8F8F7] rounded-[8px] overflow-hidden py-2">
        <Table className="w-full table-fixed">
          {/* 表头 */}
          {columns.length > 0 && (
            <TableHeader>
              <TableRow className="border-0 hover:bg-transparent">
                {columns.map((col, i) => (
                  <TableHead
                    key={i}
                    className={cn(
                      'px-3 py-2 text-[12px] font-medium text-[#737373] leading-[1.5] border-0',
                      i === 0
                        ? 'text-left w-[140px]'
                        : i === totalCols - 1
                          ? 'text-left w-[90px]'
                          : 'text-left w-[70px]'
                    )}
                  >
                    <Ellipse text={col} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          )}

          {/* 数据行 */}
          <TableBody>
            {rows.map((row, rIdx) => (
              <TableRow
                key={rIdx}
                className={cn(
                  'hover:bg-transparent',
                  rIdx < rows.length - 1 ? 'border-b border-[#E5E5E5]' : 'border-0'
                )}
              >
                {row.map((cell, cIdx) => {
                  const isLastCol = cIdx === (totalCols || row.length) - 1;
                  return (
                    <TableCell
                      key={cIdx}
                      className={cn(
                        'px-3 py-2 text-[14px] font-normal leading-[1.5] border-0',
                        cIdx === 0 ? 'text-left w-[140px]' : isLastCol ? 'text-left w-[90px]' : 'text-left w-[70px]',
                        isLastCol ? getLastColColorClass(cell) : 'text-[#262626]'
                      )}
                    >
                      <Ellipse text={cell} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
